import React, {useEffect, useRef, useState} from 'react';
import './MarkoComponent.css';
import {
    COVER_LETTER,
    EDUCATION,
    ENGAGEMENTS,
    EXPERIENCE,
    INDUSTRIES,
    LINKS,
    PROFILE,
    SKILL_GROUPS,
    STATS,
} from './markoData';

/* ─── Scroll reveal hook ─────────────────────────────────────── */
function useReveal<T extends HTMLElement>() {
    const ref = useRef<T>(null);
    const [shown, setShown] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        setShown(true);
                        io.disconnect();
                    }
                });
            },
            {threshold: 0.15},
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);
    return {ref, shown};
}

/* ─── Animated counter ───────────────────────────────────────── */
const Counter: React.FC<{ value: number; suffix: string; run: boolean }> = ({value, suffix, run}) => {
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!run) return;
        let raf = 0;
        const start = performance.now();
        const dur = 1200;
        const tick = (t: number) => {
            const p = Math.min((t - start) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.round(eased * value));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [run, value]);
    return <>{n}{suffix}</>;
};

/* ─── Rotating role text ─────────────────────────────────────── */
const RotatingRole: React.FC = () => {
    const [i, setI] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setI((v) => (v + 1) % PROFILE.roles.length), 2600);
        return () => clearInterval(id);
    }, []);
    return (
        <span className="mk-role-rotator">
            {PROFILE.roles.map((r, idx) => (
                <span key={r} className={`mk-role-item ${idx === i ? 'is-active' : ''}`}>{r}</span>
            ))}
        </span>
    );
};

/* ─── Section wrapper with reveal ────────────────────────────── */
const Section: React.FC<{ id?: string; className?: string; children: React.ReactNode }> = ({id, className, children}) => {
    const {ref, shown} = useReveal<HTMLElement>();
    return (
        <section id={id} ref={ref} className={`mk-section ${className ?? ''} ${shown ? 'is-in' : ''}`}>
            {children}
        </section>
    );
};

export const MarkoComponent: React.FC = () => {
    const heroRef = useRef<HTMLDivElement>(null);
    const [openExp, setOpenExp] = useState<string | null>(EXPERIENCE[0]?.id ?? null);
    const {ref: statsRef, shown: statsShown} = useReveal<HTMLDivElement>();

    // Mouse-follow glow in hero
    useEffect(() => {
        const el = heroRef.current;
        if (!el) return;
        const onMove = (e: MouseEvent) => {
            const r = el.getBoundingClientRect();
            el.style.setProperty('--mx', `${e.clientX - r.left}px`);
            el.style.setProperty('--my', `${e.clientY - r.top}px`);
        };
        el.addEventListener('mousemove', onMove);
        return () => el.removeEventListener('mousemove', onMove);
    }, []);

    const initials = PROFILE.name.split(' ').map((w) => w[0]).join('');

    return (
        <div className="mk">
            {/* ─── Hero ─────────────────────────────────────────── */}
            <header className="mk-hero" ref={heroRef}>
                <div className="mk-hero-glow-follow"/>
                <div className="mk-hero-glow"/>
                <div className="mk-hero-noise"/>
                <div className="mk-hero-inner">
                    <div className="mk-avatar">
                        <span>{initials}</span>
                        <span className="mk-avatar-ring"/>
                    </div>
                    <div className="mk-badge">
                        <span className="material-symbols-outlined mk-badge-dot">circle</span>
                        Open to opportunities · Remote
                    </div>
                    <h1 className="mk-hero-name">{PROFILE.name}</h1>
                    <div className="mk-hero-role">
                        <RotatingRole/>
                    </div>
                    <p className="mk-hero-tagline">{PROFILE.tagline}</p>
                    <div className="mk-hero-meta">
                        <span className="material-symbols-outlined">location_on</span>{PROFILE.location}
                    </div>
                    <div className="mk-hero-actions">
                        <a className="mk-btn mk-btn-primary" href="#contact">
                            <span className="material-symbols-outlined">mail</span>Get in touch
                        </a>
                        <a className="mk-btn mk-btn-ghost" href="#experience">
                            <span className="material-symbols-outlined">work_history</span>View experience
                        </a>
                    </div>
                    <div className="mk-socials">
                        <a className="mk-social" href={LINKS.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M12 .5A11.5 11.5 0 0 0 .5 12a11.5 11.5 0 0 0 7.86 10.92c.58.11.79-.25.79-.56v-2c-3.2.7-3.88-1.37-3.88-1.37-.53-1.34-1.3-1.7-1.3-1.7-1.06-.72.08-.71.08-.71 1.17.08 1.79 1.2 1.79 1.2 1.04 1.79 2.73 1.27 3.4.97.1-.76.41-1.27.74-1.56-2.55-.29-5.23-1.28-5.23-5.7 0-1.26.45-2.29 1.19-3.1-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.8 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.23 2.76.11 3.05.74.81 1.19 1.84 1.19 3.1 0 4.43-2.69 5.4-5.25 5.69.42.36.79 1.08.79 2.18v3.23c0 .31.21.68.8.56A11.5 11.5 0 0 0 23.5 12 11.5 11.5 0 0 0 12 .5"/></svg>
                        </a>
                        <a className="mk-social" href={LINKS.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path fill="currentColor" d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.8 0 0 .78 0 1.75v20.5C0 23.22.8 24 1.77 24h20.45C23.2 24 24 23.22 24 22.25V1.75C24 .78 23.2 0 22.22 0z"/></svg>
                        </a>
                    </div>
                </div>
                <a href="#story" className="mk-scroll-hint" aria-label="Scroll down">
                    <span className="material-symbols-outlined">keyboard_arrow_down</span>
                </a>
            </header>

            {/* ─── Stats ────────────────────────────────────────── */}
            <div className="mk-stats" ref={statsRef}>
                <div className="mk-stats-inner">
                    {STATS.map((s, i) => (
                        <React.Fragment key={s.label}>
                            {i > 0 && <div className="mk-stat-div"/>}
                            <div className="mk-stat">
                                <div className="mk-stat-num">
                                    <Counter value={s.value} suffix={s.suffix} run={statsShown}/>
                                </div>
                                <div className="mk-stat-label">{s.label}</div>
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* ─── Cover letter / story ─────────────────────────── */}
            <Section id="story" className="mk-story">
                <div className="mk-container mk-story-grid">
                    <div className="mk-story-aside">
                        <p className="mk-eyebrow">The Short Version</p>
                        <h2 className="mk-h2">Why we should<br/>work together</h2>
                        <div className="mk-signature">
                            <span className="mk-sig-name">{PROFILE.name}</span>
                            <span className="mk-sig-role">{PROFILE.title}</span>
                        </div>
                    </div>
                    <div className="mk-story-body">
                        {COVER_LETTER.map((p, i) => (
                            <p key={i} className="mk-story-p" style={{transitionDelay: `${i * 90}ms`}}>{p}</p>
                        ))}
                    </div>
                </div>
            </Section>

            {/* ─── Experience ───────────────────────────────────── */}
            <Section id="experience" className="mk-exp">
                <div className="mk-container">
                    <div className="mk-section-head">
                        <p className="mk-eyebrow">Career</p>
                        <h2 className="mk-h2">Experience</h2>
                        <p className="mk-sub">Leading engineering across healthcare, fintech, streaming, IoT and consumer products.</p>
                    </div>
                    <div className="mk-timeline">
                        {EXPERIENCE.map((exp) => {
                            const open = openExp === exp.id;
                            return (
                                <div key={exp.id} className={`mk-tl-item ${open ? 'is-open' : ''} ${exp.current ? 'is-current' : ''}`}>
                                    <div className="mk-tl-rail">
                                        <span className="mk-tl-dot"/>
                                    </div>
                                    <div className="mk-tl-card">
                                        <button
                                            className="mk-tl-header"
                                            onClick={() => setOpenExp(open ? null : exp.id)}
                                            aria-expanded={open}
                                        >
                                            <div className="mk-tl-top">
                                                <div>
                                                    <h3 className="mk-tl-role">{exp.role}</h3>
                                                    <div className="mk-tl-org">
                                                        {exp.org}
                                                        {exp.current && <span className="mk-tl-now">Current</span>}
                                                    </div>
                                                </div>
                                                <div className="mk-tl-right">
                                                    <span className="mk-tl-period">{exp.period}</span>
                                                    <span className="material-symbols-outlined mk-tl-chev">expand_more</span>
                                                </div>
                                            </div>
                                        </button>
                                        <div className="mk-tl-detail">
                                            <div className="mk-tl-detail-wrap">
                                                <ul className="mk-tl-bullets">
                                                    {exp.bullets.map((b, i) => (
                                                        <li key={i}>
                                                            <span className="material-symbols-outlined">check_circle</span>{b}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Section>

            {/* ─── Skills ───────────────────────────────────────── */}
            <Section id="skills" className="mk-skills">
                <div className="mk-container">
                    <div className="mk-section-head">
                        <p className="mk-eyebrow">Toolbox</p>
                        <h2 className="mk-h2">Skills & technologies</h2>
                        <p className="mk-sub">A modern, adaptable stack — chosen to fit the project and the team.</p>
                    </div>
                    <div className="mk-skill-grid">
                        {SKILL_GROUPS.map((g) => (
                            <div key={g.label} className="mk-skill-card">
                                <div className="mk-skill-head">
                                    <span className="mk-skill-icon material-symbols-outlined">{g.icon}</span>
                                    <h3 className="mk-skill-title">{g.label}</h3>
                                </div>
                                <div className="mk-chips">
                                    {g.skills.map((s) => <span key={s} className="mk-chip">{s}</span>)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* ─── Personal Engagements & Products ──────────────── */}
            <Section id="products" className="mk-products">
                <div className="mk-container">
                    <div className="mk-section-head">
                        <p className="mk-eyebrow">Built & Shipped</p>
                        <h2 className="mk-h2">Personal Engagements &amp; Products</h2>
                    </div>
                    <div className="mk-eng-grid">
                        {ENGAGEMENTS.map((e) => (
                            <div key={e.name} className="mk-eng-card">
                                <div className="mk-eng-head">
                                    <div className="mk-product-icon">
                                        <span className="material-symbols-outlined">{e.icon}</span>
                                    </div>
                                    <div className="mk-eng-headtext">
                                        <div className="mk-product-name">
                                            {e.name}
                                            {e.badge && (
                                                <span className="mk-product-badge">
                                                    <span className="material-symbols-outlined">verified</span>{e.badge}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mk-eng-meta">{e.role} · {e.period}</div>
                                    </div>
                                    {e.url && (
                                        <a
                                            className="mk-eng-link"
                                            href={e.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Open ${e.name}`}
                                        >
                                            <span className="material-symbols-outlined">north_east</span>
                                        </a>
                                    )}
                                </div>
                                <ul className="mk-tl-bullets mk-eng-bullets">
                                    {e.bullets.map((b, i) => (
                                        <li key={i}>
                                            <span className="material-symbols-outlined">check_circle</span>{b}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* ─── Reach / footprint ────────────────────────────── */}
            <Section className="mk-reach">
                <div className="mk-container mk-reach-grid">
                    <div className="mk-reach-card">
                        <span className="mk-reach-icon material-symbols-outlined">category</span>
                        <h3 className="mk-reach-title">Industries delivered</h3>
                        <div className="mk-country-list">
                            {INDUSTRIES.map((c) => <span key={c} className="mk-country">{c}</span>)}
                        </div>
                    </div>
                    <div className="mk-reach-card">
                        <span className="mk-reach-icon material-symbols-outlined">school</span>
                        <h3 className="mk-reach-title">Education</h3>
                        <p className="mk-reach-degree">{EDUCATION.degree}</p>
                        <p className="mk-reach-major">{EDUCATION.major}</p>
                    </div>
                </div>
            </Section>

            {/* ─── Contact / CTA ────────────────────────────────── */}
            <Section id="contact" className="mk-contact">
                <div className="mk-contact-glow"/>
                <div className="mk-container mk-contact-inner">
                    <p className="mk-eyebrow">Let's talk</p>
                    <h2 className="mk-h2">Ready to build something great</h2>
                    <p className="mk-sub">I'd welcome the chance to help your team ship high-quality mobile products.</p>
                    <div className="mk-hero-actions mk-contact-actions">
                        <a className="mk-btn mk-btn-primary" href={`mailto:${PROFILE.email}`}>
                            <span className="material-symbols-outlined">mail</span>{PROFILE.email}
                        </a>
                        <a className="mk-btn mk-btn-ghost" href={LINKS.linkedin} target="_blank" rel="noopener noreferrer">
                            <span className="material-symbols-outlined">link</span>LinkedIn
                        </a>
                        <a className="mk-btn mk-btn-ghost" href={LINKS.github} target="_blank" rel="noopener noreferrer">
                            <span className="material-symbols-outlined">code</span>GitHub
                        </a>
                    </div>
                    <p className="mk-foot">© {new Date().getFullYear()} {PROFILE.name}</p>
                </div>
            </Section>
        </div>
    );
};
